import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RbacFormField, RbacFormFieldOption } from './rbac-form-field.model';

@Component({
  selector: 'app-rbac-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,FormsModule],
  templateUrl: './rbac-form.component.html',
  styleUrls: ['./rbac-form.component.scss']
})
export class RbacFormComponent implements OnChanges {
  @Input() title = '';
  @Input() fields: RbacFormField[] = [];
  @Input() model: Record<string, any> | null = null; // null => add mode
  @Input() saving = false;
  openDropdownKey: string | null = null; // which multiselect / icon-select panel is open
  iconSearchTerm: Record<string, string> = {}; // per-field search text for icon-select

  @Output() save = new EventEmitter<Record<string, any>>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup = this.fb.group({});

  readonly defaultSpritePath = 'assets/svg/icon-sprite.svg';

  constructor(private fb: FormBuilder) {}

  get isEdit(): boolean {
    return this.model?.['_id'];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['model']) {
      this.buildForm();
    }
  }

  toggleDropdown(key: string): void {
    const opening = this.openDropdownKey !== key;
    this.openDropdownKey = opening ? key : null;
    if (opening) {
      this.iconSearchTerm[key] = ''; // reset filter each time it's opened
    }
  }

  closeDropdown(): void {
    this.openDropdownKey = null;
  }

  private selectedValues(key: string): string[] {
    const raw = this.form.get(key)?.value ?? '';
    return raw ? String(raw).split(',').filter(Boolean) : [];
  }

  isMultiSelected(key: string, value: any): boolean {
    return this.selectedValues(key).includes(String(value));
  }

  toggleMultiSelect(key: string, value: any): void {
    const control = this.form.get(key);
    if (!control) { return; }
    const current = this.selectedValues(key);
    const strValue = String(value);
    const next = current.includes(strValue)
      ? current.filter((v) => v !== strValue)
      : [...current, strValue];
    control.setValue(next.join(','));
  }

  multiSelectLabel(field: RbacFormField): string {
    const selected = this.selectedValues(field.key);
    if (!selected.length) { return field.placeholder || 'Select...'; }
    const labels = (field.options || [])
      .filter((o) => selected.includes(String(o.value)))
      .map((o) => o.label);
    return labels.join(', ');
  }

  // --- icon-select helpers ---

  selectedIconOption(field: RbacFormField): RbacFormFieldOption | undefined {
    const value = this.form.get(field.key)?.value;
    if (!value) { return undefined; }
    return (field.options || []).find((o) => String(o.value) === String(value));
  }

  selectIconOption(key: string, value: any): void {
    const control = this.form.get(key);
    if (!control) { return; }
    control.setValue(value);
    this.closeDropdown();
  }

  iconHref(field: RbacFormField, value: any): string {
    const sprite = field.spritePath || this.defaultSpritePath;
    const opt = (field.options || []).find((o) => String(o.value) === String(value));
    const symbolId = opt?.rawId || value; // fall back to value if rawId isn't set
    return `${sprite}#${symbolId}`;
  }

  onIconSearchInput(key: string, term: string): void {
    this.iconSearchTerm[key] = term;
  }

  filteredIconOptions(field: RbacFormField): RbacFormFieldOption[] {
    const term = (this.iconSearchTerm[field.key] || '').trim().toLowerCase();
    const options = field.options || [];
    if (!term) { return options; }
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }

  // --- shared ---

  private buildForm(): void {
  const group: Record<string, any> = {};
  for (const field of this.fields) {
    const existingValue = this.model ? this.model[field.key] : undefined;
    const defaultValue =
      existingValue !== undefined
        ? existingValue
        : field.default !== undefined
        ? field.default
        : field.type === 'toggle'
        ? 'N'
        : field.type === 'multiselect'
        ? ''
        : '';

    const validators = field.required ? [Validators.required] : [];
    const disabled = this.isEdit && field.disabledOnEdit;
    group[field.key] = this.fb.control({ value: defaultValue, disabled }, validators);
  }
  if (this.isEdit) {
    group['_id'] = this.fb.control(this.model!['_id']);
  }
  this.form = this.fb.group(group);
}

  toggle(key: string): void {
    const control = this.form.get(key);
    if (!control) { return; }
    control.setValue(control.value === 'Y' ? 'N' : 'Y');
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const rawValue = this.form.getRawValue();
    if (this.isEdit) {
      for (const field of this.fields) {
        if (field.NoPayloadEdit === false) {
          delete rawValue[field.key];
        }
      }
    }
    this.save.emit(rawValue);
  }

  onCancel(): void {
    this.cancel.emit();
  }
}